import React, { useState, useCallback, useEffect } from "react"
import { useToast } from "src/components/ui/toast"
import { TagPicker } from "./TagPicker"
import { addTagging, removeTagging } from "../api/tagsApi"

/**
 * Inline tags section for entity drawers and detail pages.
 *
 * Props:
 *   taggableType  – "Lead" | "Contact" | "Project" | "Document" | "Deal" | "Booking"
 *   taggableId    – record id
 *   initialTags   – array of { id, name, color } already on this record
 *   variant       – "section" (default, bordered block for drawers) | "inline" (bare)
 *   showLabel     – render the "Tags" caption (default: variant === "section")
 *   onChanged()   – called after each successful add/remove so parent can refetch
 */
export function TagEntitySection({
  taggableType,
  taggableId,
  initialTags = [],
  variant = "section",
  showLabel,
  onChanged,
}) {
  const { toast } = useToast()
  const [appliedTags, setAppliedTags] = useState(initialTags)
  const [busy, setBusy] = useState(false)

  // Re-seed when navigating between records of the same type.
  useEffect(() => { setAppliedTags(initialTags) }, [taggableId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = useCallback(async (tag) => {
    if (busy) return
    setBusy(true)
    try {
      await addTagging({ tag_id: tag.id, taggable_type: taggableType, taggable_id: taggableId })
      setAppliedTags((prev) => [...prev, tag])
      onChanged?.()
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not add tag" })
    } finally {
      setBusy(false)
    }
  }, [busy, taggableType, taggableId, onChanged, toast])

  const handleRemove = useCallback(async (tag) => {
    if (busy) return
    setBusy(true)
    try {
      await removeTagging({ tag_id: tag.id, taggable_type: taggableType, taggable_id: taggableId })
      setAppliedTags((prev) => prev.filter((t) => t.id !== tag.id))
      onChanged?.()
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not remove tag" })
    } finally {
      setBusy(false)
    }
  }, [busy, taggableType, taggableId, onChanged, toast])

  const isInline = variant === "inline"
  const withLabel = showLabel ?? !isInline

  return (
    <div className={isInline ? "" : "px-6 py-4 border-t border-slate-100"}>
      {withLabel && (
        <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-slate-400">Tags</p>
      )}
      <TagPicker
        appliedTags={appliedTags}
        onAdd={handleAdd}
        onRemove={handleRemove}
        disabled={busy}
      />
    </div>
  )
}
